import _extends from '@babel/runtime/helpers/extends';
import classNames from 'classnames';
import React, { forwardRef, useId } from 'react';
import { faXmark, faDownload, faGlobe, faUser, faSpinner, faBars, faEnvelope, faCommentSms, faCirclePlus, faCircleInfo, faCircleExclamation, faChevronRight, faChevronLeft, faChevronDown, faChevronUp, faCheck, faCaretRight, faSquareArrowUpRight, faArrowDownLong, faArrowUpLong, faArrowDown, faArrowUp, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { faXTwitter, faTwitter, faFacebookF } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select, { components } from 'react-select';

const Icon = _ref => {
  let {
    className,
    icon
  } = _ref;
  let faIcon;
  switch (icon) {
    case 'arrowLeft':
      faIcon = faArrowLeft;
      break;
    case 'arrowRight':
      faIcon = faArrowRight;
      break;
    case 'arrowUp':
      faIcon = faArrowUp;
      break;
    case 'arrowDown':
      faIcon = faArrowDown;
      break;
    case 'arrowUpLong':
      faIcon = faArrowUpLong;
      break;
    case 'arrowDownLong':
      faIcon = faArrowDownLong;
      break;
    case 'squareArrowUpRight':
      faIcon = faSquareArrowUpRight;
      break;
    case 'caretRight':
      faIcon = faCaretRight;
      break;
    case 'check':
      faIcon = faCheck;
      break;
    case 'chevronUp':
      faIcon = faChevronUp;
      break;
    case 'chevronDown':
      faIcon = faChevronDown;
      break;
    case 'chevronLeft':
      faIcon = faChevronLeft;
      break;
    case 'chevronRight':
      faIcon = faChevronRight;
      break;
    case 'circleExclamation':
      faIcon = faCircleExclamation;
      break;
    case 'circleInfo':
      faIcon = faCircleInfo;
      break;
    case 'circlePlus':
      faIcon = faCirclePlus;
      break;
    case 'facebook':
      faIcon = faFacebookF;
      break;
    case 'twitter':
      faIcon = faTwitter;
      break;
    case 'xTwitter':
      faIcon = faXTwitter;
      break;
    case 'sms':
      faIcon = faCommentSms;
      break;
    case 'envelope':
      faIcon = faEnvelope;
      break;
    case 'bars':
      faIcon = faBars;
      break;
    case 'spinner':
      faIcon = faSpinner;
      break;
    case 'user':
      faIcon = faUser;
      break;
    case 'globe':
      faIcon = faGlobe;
      break;
    case 'download':
      faIcon = faDownload;
      break;
    case 'xmark':
      faIcon = faXmark;
      break;
    default:
      console.warn("".concat(icon, " is not a supported icon name."));
      return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    className: className,
    icon: faIcon
  });
};
Icon.displayName = 'Icon';

const Link = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    icon,
    children,
    className,
    ...props
  } = _ref;
  const linkClassNames = classNames('jfcl-link', className);
  const opensInNewTab = props.target === '_blank';
  // When using this component within a react-router-dom <Link />, a navigate function is passed through the props us here.
  // Since this navigate function is not a normal anchor element attribute we want to remove it before applying the rest of
  // the props to avoid browser errors.
  // Github issue talking about what's happening:
  // https://github.com/remix-run/react-router/issues/6962
  // Reference to react-router-dom Link component code to see what it's doing:
  // https://github.com/remix-run/react-router/blob/0c058adc5b56c6d9b409af5e4d8330c645fac00c/packages/react-router-dom/modules/Link.js
  delete props.navigate;
  return /*#__PURE__*/React.createElement("a", _extends({}, props, {
    ref: ref,
    className: linkClassNames
  }), children, icon === 'internal' && /*#__PURE__*/React.createElement(Icon, {
    icon: "arrowRight",
    className: "jfcl-link__icon"
  }), icon === 'external' && /*#__PURE__*/React.createElement(Icon, {
    icon: "squareArrowUpRight",
    className: "jfcl-link__icon"
  }), opensInNewTab && /*#__PURE__*/React.createElement("span", {
    className: "jfcl-sr-only"
  }, "(Opens in a new window)"));
});
Link.displayName = 'Link';

const Alert = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    type = 'info',
    variant = 'primary',
    text,
    action,
    actionHref,
    actionLabel,
    className,
    ...props
  } = _ref;
  const alertClassNames = classNames('jfcl-alert', "jfcl-variant-".concat(variant), "jfcl-type-".concat(type), className);
  const hasAction = !!action;
  const showButton = hasAction && !actionHref;
  const showLink = actionLabel && actionHref;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: ref,
    "data-testid": "jfcl-alert",
    className: alertClassNames
  }, props), /*#__PURE__*/React.createElement("div", {
    className: "jfcl-alert__content"
  }, type === 'error' && /*#__PURE__*/React.createElement(Icon, {
    icon: "circleExclamation",
    className: "jfcl-alert__icon jfcl-alert__icon-error",
    "data-testid": "jfcl-alert__icon"
  }), type === 'success' && /*#__PURE__*/React.createElement(Icon, {
    icon: "check",
    className: "jfcl-alert__icon",
    "data-testid": "jfcl-alert__icon"
  }), text), showButton && /*#__PURE__*/React.createElement("button", {
    className: actionLabel ? 'jfcl-alert__btn jfcl-alert__btn_text' : 'jfcl-alert__btn',
    "data-testid": "jfcl-alert__btn",
    onClick: action
  }, actionLabel || /*#__PURE__*/React.createElement(Icon, {
    icon: "xmark",
    "aria-label": "Dismiss"
  })), showLink && /*#__PURE__*/React.createElement(Link, _extends({
    href: actionHref,
    className: actionLabel ? 'jfcl-alert__btn  jfcl-alert__btn_text' : 'jfcl-alert__btn',
    "data-testid": "jfcl-alert__btn"
  }, hasAction && {
    onClick: action
  }), actionLabel));
});
Alert.displayName = 'Alert';

/**
 * Accepts all `ButtonHTMLAttributes`
 */
const Button = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    labelText,
    variant = 'primary',
    size = 'large',
    loading,
    labelIcon,
    iconOnRight,
    iconOnly,
    className,
    ...props
  } = _ref;
  const buttonClassNames = classNames('jfcl-button', "jfcl-variant-".concat(variant), "jfcl-size-".concat(size), "".concat(loading ? 'jfcl-is-loading' : ''), "".concat(iconOnly ? 'jfcl-btn-icon-only' : ''), className);
  const iconClassNames = classNames('jfcl-button__icon', "".concat(iconOnRight ? 'jfcl-button__icon_right' : ''));
  const arias = {};
  if (iconOnly) {
    arias['aria-label'] = labelText;
  }
  return /*#__PURE__*/React.createElement("button", _extends({
    ref: ref,
    type: "button",
    className: buttonClassNames,
    "data-testid": "jfcl-button"
  }, arias, props), /*#__PURE__*/React.createElement("div", {
    className: "jfcl-button__label"
  }, loading && /*#__PURE__*/React.createElement("span", {
    className: iconClassNames
  }), !loading && labelIcon ? /*#__PURE__*/React.createElement("span", {
    className: iconClassNames
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: labelIcon
  })) : null, !iconOnly && /*#__PURE__*/React.createElement("span", {
    className: "jfcl-button__text"
  }, labelText)));
});
Button.displayName = 'Button';

const HeaderLabel = _ref => {
  let {
    labelFor,
    isLegend,
    hasHelperText,
    headerText
  } = _ref;
  const headerLabelClassName = classNames('jfcl-input-header__label', {
    ['jfcl-input-header__label--has-helper']: hasHelperText
  });
  if (labelFor) {
    return /*#__PURE__*/React.createElement("label", {
      htmlFor: labelFor,
      className: headerLabelClassName,
      "data-testid": "header-label"
    }, headerText);
  } else if (isLegend) {
    return /*#__PURE__*/React.createElement("legend", {
      className: headerLabelClassName,
      "data-testid": "header-legend"
    }, headerText);
  } else {
    return /*#__PURE__*/React.createElement("div", {
      className: headerLabelClassName,
      "data-testid": "header-div"
    }, headerText);
  }
};
const InputHeader = _ref2 => {
  let {
    labelFor,
    isLegend,
    headerText,
    helperText,
    invalid,
    invalidText
  } = _ref2;
  const showHeader = headerText || helperText || invalid && invalidText;
  return /*#__PURE__*/React.createElement(React.Fragment, null, showHeader && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderLabel, {
    labelFor: labelFor,
    isLegend: isLegend,
    headerText: headerText,
    hasHelperText: !!helperText
  }), helperText && /*#__PURE__*/React.createElement("div", {
    className: "jfcl-input-header__helper_text",
    "data-testid": "helper-text"
  }, helperText), invalid && invalidText && /*#__PURE__*/React.createElement("div", {
    className: "jfcl-input-header__invalid_text",
    "data-testid": "error"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "circleExclamation",
    className: "jfcl-input-header__invalid_icon"
  }), invalidText)));
};

/**
 * Accepts all `InputHTMLAttributes`
 */
const Checkbox = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    className,
    invalidText,
    invalid,
    headerText,
    helperText,
    labelText,
    disabled,
    id,
    ...props
  } = _ref;
  const checkboxClassNames = classNames('jfcl-checkbox', {
    ['jfcl-checkbox--is-disabled']: disabled
  }, className);
  const checkmarkClassNames = classNames('jfcl-checkbox__checkmark', {
    ['jfcl-checkbox__checkmark--is-invalid']: invalid
  }, className);
  return /*#__PURE__*/React.createElement("div", {
    className: checkboxClassNames,
    "data-testid": "jfcl-checkbox"
  }, /*#__PURE__*/React.createElement(InputHeader, {
    headerText: headerText,
    helperText: helperText,
    invalid: invalid,
    invalidText: invalidText
  }), /*#__PURE__*/React.createElement("div", {
    className: "jfcl-checkbox__checkmark-label-wrapper"
  }, /*#__PURE__*/React.createElement("input", _extends({
    ref: ref
  }, props, {
    id: id,
    className: "jfcl-checkbox__input",
    type: "checkbox"
  })), /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    className: "jfcl-checkbox__label",
    "data-testid": "jfcl-checkbox__label"
  }, /*#__PURE__*/React.createElement("span", {
    className: checkmarkClassNames,
    "data-testid": "jfcl-checkbox__checkmark",
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "check",
    className: "jfcl-checkbox__check_icon"
  })), labelText)));
});
Checkbox.displayName = 'Checkbox';

const DropdownIndicator = props => {
  return /*#__PURE__*/React.createElement(components.DropdownIndicator, props, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronDown
  }));
};
const Dropdown = _ref => {
  let {
    className,
    helperText,
    invalid,
    invalidText,
    disabled,
    labelText,
    onChange,
    options,
    ...props
  } = _ref;
  const hasOnChange = !!onChange;

  // Randomly generated id
  const instanceId = useId();

  // format of the id on the actual <input /> element created by react-select
  // using the instanceID prop
  const inputId = "react-select-".concat(instanceId, "-input");
  const dropdownClassNames = classNames('jfcl-dropdown', {
    ['jfcl-dropdown--is-invalid']: invalid
  }, className);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputHeader, {
    headerText: labelText,
    labelFor: inputId,
    helperText: helperText,
    invalid: invalid,
    invalidText: invalidText
  }), /*#__PURE__*/React.createElement(Select, _extends({}, props, {
    instanceId: instanceId,
    className: dropdownClassNames,
    classNamePrefix: "jfcl-dropdown",
    placeholder: null,
    isDisabled: disabled,
    options: options
  }, hasOnChange && {
    onChange: onChange
  }, {
    components: {
      DropdownIndicator
    }
  })));
};
Dropdown.displayName = 'Dropdown';

/**
 * Accepts all `HTMLFieldSetElement`
 */
const FormGroup = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    className,
    children,
    invalidText,
    invalid,
    helperText,
    legendText,
    ...props
  } = _ref;
  const fieldsetClassNames = classNames('jfcl-form-group', {
    ['jfcl-form-group-is-invalid']: invalid
  }, className);
  return /*#__PURE__*/React.createElement("fieldset", _extends({
    ref: ref
  }, props, {
    className: fieldsetClassNames
  }), /*#__PURE__*/React.createElement(InputHeader, {
    headerText: legendText,
    helperText: helperText,
    invalid: invalid,
    invalidText: invalidText,
    isLegend: true
  }), children);
});
FormGroup.displayName = 'FormGroup';

/**
 * Accepts all `InputHTMLAttributes`
 */
const RadioButton = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    className,
    invalidText,
    invalid,
    headerText,
    helperText,
    labelText,
    disabled,
    id,
    ...props
  } = _ref;
  const radioButtonClassNames = classNames('jfcl-radio-button', {
    ['jfcl-radio-button--is-invalid']: invalid,
    ['jfcl-radio-button--is-disabled']: disabled
  }, className);
  const checkmarkClassNames = classNames('jfcl-radio-button__checkmark', {
    ['jfcl-radio-button__checkmark--is-invalid']: invalid
  }, className);
  return /*#__PURE__*/React.createElement("div", {
    className: radioButtonClassNames,
    "data-testid": "jfcl-radio-button"
  }, /*#__PURE__*/React.createElement(InputHeader, {
    headerText: headerText,
    helperText: helperText,
    invalid: invalid,
    invalidText: invalidText
  }), /*#__PURE__*/React.createElement("input", _extends({
    ref: ref
  }, props, {
    id: id,
    className: "jfcl-radio-button__input",
    type: "radio"
  })), /*#__PURE__*/React.createElement("label", {
    htmlFor: id,
    className: "jfcl-radio-button__label",
    "data-testid": "jfcl-radio-button__label"
  }, /*#__PURE__*/React.createElement("span", {
    className: checkmarkClassNames,
    "data-testid": "jfcl-radio-button__checkmark"
  }, /*#__PURE__*/React.createElement("span", {
    className: "jfcl-radio-button__checkmark-checked"
  })), labelText));
});
RadioButton.displayName = 'RadioButton';

/**
 * Accepts all `InputHTMLAttributes`
 */
const TextInput = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    className,
    invalidText,
    invalid,
    helperText,
    labelText,
    size,
    id,
    type = 'text',
    ...props
  } = _ref;
  const textInputWrapperClassNames = classNames('jfcl-text-input', {
    ['jfcl-text-input--is-invalid']: invalid
  }, className);
  const textInputClassNames = classNames('jfcl-text-input__input', {
    ["jfcl-text-input--size-".concat(size)]: size
  });
  return /*#__PURE__*/React.createElement("div", {
    className: textInputWrapperClassNames,
    "data-testid": "jfcl-text-input"
  }, /*#__PURE__*/React.createElement(InputHeader, {
    headerText: labelText,
    helperText: helperText,
    invalidText: invalidText,
    invalid: invalid,
    labelFor: id
  }), /*#__PURE__*/React.createElement("input", _extends({
    ref: ref
  }, props, {
    id: id,
    className: textInputClassNames,
    type: type
  })));
});
TextInput.displayName = 'TextInput';

export { Alert, Button, Checkbox, Dropdown, FormGroup, Icon, Link, RadioButton, TextInput };
